import { SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import { v4 as uuidv4 } from 'uuid';
import { remotionPlayerStateStore } from '../store/remotionPlayerState';
import { getAbsolutePlayerTime, getPreviousSegmentAbsoluteTime } from '../timeUtils';
import { getClipCompositeSegment } from './compositeUtils';
import { LayoutDisableReason, LayoutType, FitType } from '@/player-config/src/types/asset-types';
import { getCompositeSegment } from '@/player-config/src/utils/playerConfigGetters';
import { getAlignedRecordings } from '@/player-config/src/utils/alignedRecordings';
import { Scene } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { ORIGINAL_VIDEO_ID } from '@/player-config/src/constants/screenshare';
import { getVideoCropPositionMap } from '@/player-config/src/utils/sceneUtils';
import { getVideosAndTimeRanges } from '@/player-config/src/utils/videoRangeUtils';
import { Clip } from '@/player-config/src/types/clip-types';
import { getClipBroadcastId, getClipFPS } from '@/player-config/src/utils/clipUtils';
import {
  isFullVideoFaceRecEnabled,
  isLayoutsEnabledForPreRecordedEventsFeatureFlag,
  isLayoutsWithVisionDataEnabled
} from '@/stores/clip';
import { isLayoutAudiogram, isLayoutDefault, isSpeakerLayout } from '@/player-config/src/utils/layout';
import { getSpeakerNameAndTitle } from '@/player-config/src/utils/speakerUtils';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import {
  selectAlignedRecordings,
  selectSpeakerDetails,
  selectVideoFeeds
} from '@/player-config/src/selectors/contentMetadataStoreSelectors';
import { getSpeakerKeyFromVideoFeed, getVideoFeedById } from '@/utils/videoFeeds';
import { VideoFeedType } from '@/player-config/src/types/video-feed-types';
import {
  buildSceneTracks,
  SCREENSHARE_TRACK_NAME,
  SHARED_VIDEO_TRACK_NAME
} from '@/stores/contentMetaDataStore/utils/sceneTracks';

export const getDefaultSceneData = ({
  clip,
  facialRecognitionToDefaultPosition
}: {
  clip: Clip;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
}): Scene => {
  const layout = clip.asset_metadata.layout as LayoutType;
  const fitMode = clip.asset_metadata.fit as FitType;
  const all_video_ids = getVideosAndTimeRanges({
    clip: clip,
    isFullFaceRecEnabled: isFullVideoFaceRecEnabled(),
    areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag(),
    contentMetadataStoreInstance: useContentMetadataStore
  }).map(video => video.videoId);

  const fps = getClipFPS(clip);
  const broadcastId = getClipBroadcastId(clip);
  const alignedRecordingsFromMetadata = broadcastId
    ? selectAlignedRecordings(broadcastId)(useContentMetadataStore.getState())
    : [];
  const alignedRecordings =
    alignedRecordingsFromMetadata.length > 0
      ? alignedRecordingsFromMetadata
      : broadcastId
        ? getAlignedRecordings({ broadcastId, contentMetadataStoreInstance: useContentMetadataStore })
        : [];
  return {
    id: uuidv4(),
    name: 'Scene 1',
    details: {
      layout,
      fitMode,
      visible_video_ids: all_video_ids,
      all_video_ids: all_video_ids,
      video_crop_position_map:
        facialRecognitionToDefaultPosition &&
        getVideoCropPositionMap(facialRecognitionToDefaultPosition, alignedRecordings)
    },
    toFrame: clip.asset_metadata.end * fps,
    fromFrame: clip.asset_metadata.start * fps
  };
};

export const createNewScene = ({ newClip, currentFrame }: { newClip: Clip; currentFrame: number }) => {
  const compositeSegment = getClipCompositeSegment(newClip);
  const layout = newClip.asset_metadata.layout as LayoutType;
  const fitMode = newClip.asset_metadata.fit as FitType;
  const all_video_ids = getVideosAndTimeRanges({
    clip: newClip,
    isFullFaceRecEnabled: isFullVideoFaceRecEnabled(),
    areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag(),
    contentMetadataStoreInstance: useContentMetadataStore
  }).map(video => video.videoId);

  return [
    {
      id: uuidv4(),
      name: 'Scene 1',
      fromFrame: compositeSegment.currentBounds.fromFrame,
      toFrame: currentFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: all_video_ids,
        all_video_ids: all_video_ids
      }
    },
    {
      id: uuidv4(),
      name: 'Scene 2',
      fromFrame: currentFrame,
      toFrame: compositeSegment.currentBounds.toFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: all_video_ids,
        all_video_ids: all_video_ids
      }
    }
  ];
};

export const createNewSceneAtRange = ({
  newClip,
  startTimeFrame,
  endTimeFrame
}: {
  newClip: Clip;
  startTimeFrame: number;
  endTimeFrame: number;
}) => {
  const compositeSegment = getClipCompositeSegment(newClip);

  const layout = newClip.asset_metadata.layout as LayoutType;
  const fitMode = newClip.asset_metadata.fit as FitType;
  const all_video_ids = getVideosAndTimeRanges({
    clip: newClip,
    isFullFaceRecEnabled: isFullVideoFaceRecEnabled(),
    areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag(),
    contentMetadataStoreInstance: useContentMetadataStore
  }).map(video => video.videoId);

  return [
    {
      id: uuidv4(),
      name: 'Scene 1',
      fromFrame: compositeSegment.currentBounds.fromFrame,
      toFrame: startTimeFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: all_video_ids,
        all_video_ids: all_video_ids
      }
    },
    {
      id: uuidv4(),
      name: 'Scene 2',
      fromFrame: startTimeFrame,
      toFrame: endTimeFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: all_video_ids,
        all_video_ids: all_video_ids
      }
    },
    {
      id: uuidv4(),
      name: 'Scene 3',
      fromFrame: endTimeFrame,
      toFrame: compositeSegment.currentBounds.toFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: all_video_ids,
        all_video_ids: all_video_ids
      }
    }
  ];
};

export const addNewSceneAtFrame = ({ newClip, currentFrame }: { newClip: Clip; currentFrame: number }) => {
  const scenes = newClip.asset_metadata.scenes || [];
  const currentSceneIndex = scenes.findIndex(scene => scene.fromFrame <= currentFrame && scene.toFrame >= currentFrame);

  // If no scene is found, return the original scenes array
  if (currentSceneIndex === -1) {
    return scenes;
  }

  const currentScene = scenes[currentSceneIndex];
  const nextScene = scenes[currentSceneIndex + 1];
  const compositeSegment = getClipCompositeSegment(newClip);

  return [
    ...scenes.slice(0, currentSceneIndex),
    { ...currentScene, toFrame: currentFrame },
    {
      id: uuidv4(),
      name: `Scene ${Math.round(Math.random() * 100)}`,
      fromFrame: currentFrame,
      toFrame: nextScene ? nextScene.fromFrame : compositeSegment.currentBounds.toFrame,
      details: {
        ...currentScene.details
      }
    },
    ...scenes.slice(currentSceneIndex + 1)
  ];
};

export const addNewSceneAtRange = ({
  newClip,
  startTimeFrame,
  endTimeFrame
}: {
  newClip: Clip;
  startTimeFrame: number;
  endTimeFrame: number;
}) => {
  const scenes = newClip.asset_metadata.scenes || [];
  const compositeSegment = getClipCompositeSegment(newClip);
  // Find scenes that overlap with the range
  const scenesToRemove: number[] = [];

  scenes.forEach((scene, index) => {
    // Check if scene completely overlaps with the range
    const completeSceneOverlaps = scene.toFrame <= endTimeFrame && scene.fromFrame >= startTimeFrame;

    if (completeSceneOverlaps) {
      scenesToRemove.push(index);
    }
  });

  // Start with all scenes that don't overlap
  const updatedScenes = scenes
    .filter((_, index) => !scenesToRemove.includes(index))
    .map(scene => {
      if (scene.fromFrame <= startTimeFrame && scene.toFrame >= startTimeFrame) {
        return {
          ...scene,
          toFrame: startTimeFrame
        };
      }
      if (scene.toFrame >= endTimeFrame && scene.fromFrame <= endTimeFrame) {
        return {
          ...scene,
          fromFrame: endTimeFrame
        };
      }
      return scene;
    });

  // Check if there's already a scene at endFrame
  const existingSceneAtEnd = updatedScenes.find(scene => scene.fromFrame === endTimeFrame);
  const nextSceneAfterEnd = updatedScenes.find(scene => scene.fromFrame > endTimeFrame);
  const layout = newClip.asset_metadata.layout as LayoutType;
  const fitMode = newClip.asset_metadata.fit as FitType;
  const all_video_ids = getVideosAndTimeRanges({
    clip: newClip,
    isFullFaceRecEnabled: isFullVideoFaceRecEnabled(),
    areLayoutsEnabledForPreRecordedEvents: isLayoutsEnabledForPreRecordedEventsFeatureFlag(),
    contentMetadataStoreInstance: useContentMetadataStore
  }).map(video => video.videoId);

  if (!existingSceneAtEnd) {
    // Create a new scene at endFrame
    updatedScenes.push({
      id: uuidv4(),
      name: `Scene ${Math.round(Math.random() * 100)}`,
      fromFrame: endTimeFrame,
      toFrame: nextSceneAfterEnd?.fromFrame || compositeSegment.currentBounds.toFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: all_video_ids,
        all_video_ids: all_video_ids
      }
    });
  }

  // Create a new scene at startFrame
  updatedScenes.push({
    id: uuidv4(),
    name: `Scene ${Math.round(Math.random() * 100)}`,
    fromFrame: startTimeFrame,
    toFrame: endTimeFrame,
    details: {
      layout,
      fitMode,
      visible_video_ids: all_video_ids,
      all_video_ids: all_video_ids
    }
  });

  // Sort scenes by fromFrame to maintain order
  updatedScenes.sort((a, b) => a.fromFrame - b.fromFrame);
  return updatedScenes;
};

export const deleteScene = ({ newClip, sceneId }: { newClip: Clip; sceneId: string }) => {
  const scenes = newClip.asset_metadata.scenes || [];
  const sceneIndex = scenes.findIndex(scene => scene.id === sceneId);

  if (sceneIndex === -1) {
    throw new Error('Scene not found');
  }

  let updatedScenes: Scene[] = [];

  if (scenes.length === 1) {
    // If this is the last scene, just remove it
    updatedScenes = [];
  } else if (sceneIndex === 0) {
    // If deleting the first scene, merge with the next scene
    const nextScene = scenes[1];
    updatedScenes = [
      {
        ...nextScene,
        fromFrame: scenes[0].fromFrame
      },
      ...scenes.slice(2)
    ];
  } else if (sceneIndex === scenes.length - 1) {
    // If deleting the last scene, merge with the previous scene
    const prevScene = scenes[sceneIndex - 1];
    updatedScenes = [
      ...scenes.slice(0, sceneIndex - 1),
      {
        ...prevScene,
        toFrame: scenes[sceneIndex].toFrame
      }
    ];
  } else {
    // If deleting a middle scene, merge with the next scene
    const prevScene = scenes[sceneIndex - 1];
    const nextScene = scenes[sceneIndex + 1];
    updatedScenes = [
      ...scenes.slice(0, sceneIndex - 1),
      {
        ...prevScene,
        toFrame: nextScene.fromFrame
      },
      ...scenes.slice(sceneIndex + 1)
    ];
  }
  return updatedScenes;
};

export const toggleVideoVisibilityFromScene = ({
  newClip,
  sceneId,
  trackName,
  videoId
}: {
  newClip: Clip;
  trackName?: string;
  sceneId?: string;
  videoId?: string;
}) => {
  if (!(trackName || videoId)) {
    throw new Error('Track ID or video ID is required');
  }
  const scenes = newClip.asset_metadata.scenes || [];
  if (!sceneId) {
    return scenes;
  }
  const sceneIndex = scenes.findIndex(scene => scene.id === sceneId);

  if (sceneIndex === -1) {
    throw new Error('Scene not found');
  }
  const broadcastId = getClipBroadcastId(newClip)!;
  const state = useContentMetadataStore.getState();
  const speakerDetails = selectSpeakerDetails(broadcastId)(state);
  const videoFeeds = selectVideoFeeds(broadcastId)(state);
  const allVideoIds = scenes[sceneIndex].details.all_video_ids;
  const visibleVideoIds = scenes[sceneIndex].details.visible_video_ids;
  const tracks = buildSceneTracks({
    scene: {
      ...scenes[sceneIndex],
      details: {
        ...scenes[sceneIndex].details,
        all_video_ids: allVideoIds,
        visible_video_ids: visibleVideoIds
      }
    },
    videoFeeds,
    speakerDetails
  });
  const tracksWithFallback =
    tracks.length > 0 && videoFeeds.length > 0
      ? tracks
      : allVideoIds.map(id => {
          const speakerDetailsById = speakerDetails;
          const videoFeed = getVideoFeedById(videoFeeds, id);
          const speakerKey = getSpeakerKeyFromVideoFeed(videoFeed) || null;
          const isUserRecording = videoFeed?.type === VideoFeedType.USER_FEED;
          const isSharedVideo = videoFeed?.type === VideoFeedType.SHARED_VIDEO;
          const isScreenshare = videoFeed?.type === VideoFeedType.SCREENSHARE;
          const speaker = speakerDetailsById[speakerKey ?? ''];

          let name = id;
          if (isUserRecording && speaker) {
            name = getSpeakerNameAndTitle(speaker).name;
          } else if (id === ORIGINAL_VIDEO_ID) {
            name = 'Original track';
          } else if (isSharedVideo) {
            name = SHARED_VIDEO_TRACK_NAME;
          } else if (isScreenshare) {
            name = SCREENSHARE_TRACK_NAME;
          }

          return {
            id,
            name,
            toggleKey: speakerKey ?? name,
            selected: visibleVideoIds.includes(id),
            bgColor: 'bg-slate-100',
            lightBgColor: 'bg-slate-100',
            color: 'text-slate-700'
          };
        });

  const newVisibleVideoIds: string[] = [];

  const toggledTrack = tracksWithFallback.find(
    track =>
      track.id === videoId || (trackName !== undefined && (track.toggleKey === trackName || track.name === trackName))
  );

  if (!toggledTrack) {
    throw new Error('Track not found');
  }

  const isScreenshareTrackToggled = toggledTrack.name === SCREENSHARE_TRACK_NAME;
  const isSharedVideoTrackToggled = toggledTrack.name === SHARED_VIDEO_TRACK_NAME;

  const anyScreenShareIsSelected = tracksWithFallback.find(
    track => track.name === SCREENSHARE_TRACK_NAME && track.selected
  );
  const anySharedVideoIsSelected = tracksWithFallback.find(
    track => track.name === SHARED_VIDEO_TRACK_NAME && track.selected
  );

  tracksWithFallback.forEach(track => {
    if (track.name === SCREENSHARE_TRACK_NAME && isScreenshareTrackToggled) {
      if (!anyScreenShareIsSelected) {
        newVisibleVideoIds.push(track.id);
      }
      return;
    }

    if (track.name === SHARED_VIDEO_TRACK_NAME && isSharedVideoTrackToggled) {
      if (!anySharedVideoIsSelected) {
        newVisibleVideoIds.push(track.id);
      }
      return;
    }

    if (track.id === toggledTrack.id ? !track.selected : track.selected) {
      newVisibleVideoIds.push(track.id);
    }
  });

  scenes[sceneIndex].details.visible_video_ids = newVisibleVideoIds;
  return scenes;
};

/**
 * Fetches the scene information at the cue point
 */
export const getCurrentScene = (clip: Clip) => {
  const currentClipMetadata = clip.asset_metadata;
  const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;

  const scenes = currentClipMetadata.scenes;
  const playerConfig = currentClipMetadata.player_config;

  if (scenes && scenes.length && playerConfig) {
    const compositeSegment = getCompositeSegment(playerConfig);
    const currentSegmentFrame =
      getAbsolutePlayerTime(currentTime, clip) * getClipFPS(clip) -
      getPreviousSegmentAbsoluteTime(compositeSegment, clip) * getClipFPS(clip) +
      compositeSegment.currentBounds.fromFrame;

    const currentScene = scenes.find(
      scene => scene.fromFrame <= currentSegmentFrame && scene.toFrame > currentSegmentFrame
    );

    return currentScene;
  }

  return;
};

export const getFitFillDisabledReasonForScene = ({ clip, scene }: { scene: Scene; clip: Clip }): string | null => {
  if (isLayoutAudiogram(scene.details.layout)) {
    return LayoutDisableReason.FIT_FILL_NOT_SUPPORTED_FOR_AUDIOGRAM;
  }
  if (isLayoutDefault(scene.details.layout)) {
    return LayoutDisableReason.FILL_NOT_SUPPORTED_FOR_ORIGINAL_LAYOUT;
  }
  if (isSpeakerLayout(scene.details.layout) && isLayoutsWithVisionDataEnabled(clip)) {
    return LayoutDisableReason.FIT_NOT_SUPPORTED_FOR_UPLOADS_WITH_SPEAKER_LAYOUT;
  }
  return null;
};

export const isFitAppliedForScene = ({ clip, scene }: { scene: Scene; clip: Clip }): boolean => {
  const disableReason = getFitFillDisabledReasonForScene({ clip, scene });
  if (disableReason === LayoutDisableReason.FILL_NOT_SUPPORTED_FOR_ORIGINAL_LAYOUT) {
    return true;
  }
  if (disableReason === LayoutDisableReason.FIT_NOT_SUPPORTED_FOR_UPLOADS_WITH_SPEAKER_LAYOUT) {
    return false;
  }
  return scene.details.fitMode === 'FIT';
};

export const trimScenes = (newClip: Clip) => {
  const { scenes = [], start, end } = newClip.asset_metadata;
  const fps = getClipFPS(newClip);

  if (scenes.length === 0) {
    return;
  }

  const fromFrame = start * fps;
  const toFrame = end * fps;

  const updatedScenes = scenes
    .map(scene => {
      return {
        ...scene,
        fromFrame: Math.min(Math.max(scene.fromFrame, fromFrame), toFrame),
        toFrame: Math.min(Math.max(scene.toFrame, fromFrame), toFrame)
      };
    })
    .filter(scene => scene.fromFrame !== scene.toFrame);

  updatedScenes[0].fromFrame = fromFrame;
  updatedScenes[updatedScenes.length - 1].toFrame = toFrame;

  newClip.asset_metadata.scenes = updatedScenes;
  return newClip;
};
